/**
 * Live map → "Historia" (route playback) strings (`lhist.*`), merged into the dictionary through `extra.ts`.
 * `lhistEn` is typed against `lhistPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const lhistPl = {
  durMin: '{m} min',
  durHourMin: '{h} h {m} min',
  error: 'Nie udało się wczytać historii przejazdów.',
  day: {
    prev: 'Poprzedni dzień',
    next: 'Następny dzień',
    hint: 'Wybierz dzień — w kafelkach: kilometry i zrealizowane wizyty.',
    pick: 'Wybierz datę',
    today: 'Dziś',
    latest: 'Ostatni dzień z danymi',
    strip: 'Ostatnie 30 dni',
    visitsShort: '{n} wiz.',
  },
  reps: {
    title: 'Handlowcy',
    selected: 'Wybrano {n} z {total}',
    all: 'Wszyscy',
    none: 'Nikt',
    empty: 'Brak handlowców w zakresie.',
    noData: 'brak danych tego dnia',
  },
  controls: {
    title: 'Widok mapy',
    track: 'Ślad',
    modeFull: 'Cały',
    modeProgress: 'Do chwili',
    modeTail: 'Ogon',
    tailLength: 'Długość ogona',
    speedColor: 'Kolor = prędkość',
    layers: 'Warstwy',
  },
  layers: {
    visits: 'Wizyty',
    geofence: 'Geofence 100 m',
    stops: 'Postoje',
    gaps: 'Luki GPS',
    arrows: 'Strzałki',
    deliveries: 'Dostawy',
  },
  speedBin: { '0': '< 5', '1': '5–30', '2': '30–60', '3': '60–90', '4': '90+ km/h' },
  map: {
    fit: 'Dopasuj',
    multi: '{n} handlowców',
    points: '{n} punktów GPS',
  },
  player: {
    clock: 'Godzina',
    kmSoFar: 'Przejechano do tej chwili',
    timeline: 'Oś czasu odtwarzania',
    restart: 'Od początku',
    prevEvent: 'Poprzednie zdarzenie',
    nextEvent: 'Następne zdarzenie',
    play: 'Odtwarzaj',
    pause: 'Pauza',
    speed: 'Prędkość',
    keys: 'Skróty: spacja — odtwarzaj / pauza, ← → — 5 min wstecz / do przodu, Shift + ← → — poprzednie / następne zdarzenie.',
  },
  kind: { visit: 'Wizyta', stop: 'Postój', gap: 'Brak sygnału GPS', delivery: 'Dostawa' },
  visitStatus: { PLANNED: 'Zaplanowana', IN_PROGRESS: 'W trakcie', COMPLETED: 'Zakończona', CANCELLED: 'Anulowana', MISSED: 'Pominięta' },
  anomaly: { SHORT: 'Zbyt krótka', NIGHT: 'Poza godzinami', OUTSIDE_GEOFENCE: 'Poza strefą klienta' },
  popup: {
    distance: 'Pozycja {m} m od klienta',
    nearCustomer: 'Blisko klienta ({m} m):',
  },
  events: {
    title: 'Zdarzenia dnia',
    count: '{n} zdarzeń w chronologii',
    empty: 'Brak zdarzeń dla wybranych handlowców.',
  },
  stats: {
    rep: 'Handlowiec',
    distance: 'Dystans',
    pings: '{n} pingów GPS',
    moving: 'W ruchu',
    stopped: 'Na postojach',
    atCustomers: 'U klientów',
    visits: 'Wizyty',
    visitsBreakdown: 'jeszcze {planned} · pominięte {missed}',
    planRealization: 'Realizacja planu',
    speed: 'Prędkość śr.',
    maxSpeed: 'maks. {v} km/h',
    pingRange: 'Pierwszy – ostatni ping',
    warnings: 'Ostrzeżenia',
    warningsBreakdown: '{gaps} luk GPS · {anomalies} anomalii wizyt',
    compareTitle: 'Porównanie handlowców',
    compareHint: '{n} handlowców tego samego dnia — oś czasu odtwarzania jest wspólna.',
  },
  csv: {
    rep: 'Handlowiec',
    type: 'Typ',
    from: 'Od',
    to: 'Do',
    minutes: 'Minuty',
    customer: 'Klient',
    status: 'Status',
    anomalies: 'Anomalie',
    lat: 'Szerokość',
    lng: 'Długość',
  },
  empty: {
    dayTitle: 'Brak danych z tego dnia',
    dayBody: 'W wybranym dniu nie zarejestrowano śladu GPS ani wizyt. Wybierz inny dzień z paska powyżej.',
    noRepTitle: 'Nie wybrano handlowca',
    noRepBody: 'Zaznacz co najmniej jednego handlowca na liście, aby odtworzyć jego przejazd.',
  },
};

export const lhistEn: Shape<typeof lhistPl> = {
  durMin: '{m} min',
  durHourMin: '{h} h {m} min',
  error: 'Could not load the route history.',
  day: {
    prev: 'Previous day',
    next: 'Next day',
    hint: 'Pick a day — tiles show kilometres and completed visits.',
    pick: 'Pick a date',
    today: 'Today',
    latest: 'Latest day with data',
    strip: 'Last 30 days',
    visitsShort: '{n} vis.',
  },
  reps: {
    title: 'Sales reps',
    selected: '{n} of {total} selected',
    all: 'All',
    none: 'None',
    empty: 'No reps in scope.',
    noData: 'no data on this day',
  },
  controls: {
    title: 'Map view',
    track: 'Track',
    modeFull: 'Whole',
    modeProgress: 'Up to now',
    modeTail: 'Tail',
    tailLength: 'Tail length',
    speedColor: 'Colour = speed',
    layers: 'Layers',
  },
  layers: {
    visits: 'Visits',
    geofence: 'Geofence 100 m',
    stops: 'Stops',
    gaps: 'GPS gaps',
    arrows: 'Arrows',
    deliveries: 'Deliveries',
  },
  speedBin: { '0': '< 5', '1': '5–30', '2': '30–60', '3': '60–90', '4': '90+ km/h' },
  map: {
    fit: 'Fit view',
    multi: '{n} reps',
    points: '{n} GPS points',
  },
  player: {
    clock: 'Time',
    kmSoFar: 'Driven so far',
    timeline: 'Playback timeline',
    restart: 'Restart',
    prevEvent: 'Previous event',
    nextEvent: 'Next event',
    play: 'Play',
    pause: 'Pause',
    speed: 'Speed',
    keys: 'Shortcuts: Space — play / pause, ← → — 5 min back / forward, Shift + ← → — previous / next event.',
  },
  kind: { visit: 'Visit', stop: 'Stop', gap: 'No GPS signal', delivery: 'Delivery' },
  visitStatus: { PLANNED: 'Planned', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', CANCELLED: 'Cancelled', MISSED: 'Missed' },
  anomaly: { SHORT: 'Too short', NIGHT: 'Outside hours', OUTSIDE_GEOFENCE: 'Outside customer zone' },
  popup: {
    distance: 'Position {m} m from the customer',
    nearCustomer: 'Near customer ({m} m):',
  },
  events: {
    title: 'Day events',
    count: '{n} events in chronological order',
    empty: 'No events for the selected reps.',
  },
  stats: {
    rep: 'Sales rep',
    distance: 'Distance',
    pings: '{n} GPS pings',
    moving: 'Moving',
    stopped: 'Stopped',
    atCustomers: 'At customers',
    visits: 'Visits',
    visitsBreakdown: '{planned} still due · {missed} missed',
    planRealization: 'Plan completion',
    speed: 'Avg speed',
    maxSpeed: 'max {v} km/h',
    pingRange: 'First – last ping',
    warnings: 'Warnings',
    warningsBreakdown: '{gaps} GPS gaps · {anomalies} visit anomalies',
    compareTitle: 'Rep comparison',
    compareHint: '{n} reps on the same day — the playback timeline is shared.',
  },
  csv: {
    rep: 'Sales rep',
    type: 'Type',
    from: 'From',
    to: 'To',
    minutes: 'Minutes',
    customer: 'Customer',
    status: 'Status',
    anomalies: 'Anomalies',
    lat: 'Latitude',
    lng: 'Longitude',
  },
  empty: {
    dayTitle: 'No data for this day',
    dayBody: 'No GPS track or visits were recorded on the selected day. Pick another day from the strip above.',
    noRepTitle: 'No rep selected',
    noRepBody: 'Tick at least one rep in the list to replay their route.',
  },
};
